import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(updateLocale);
dayjs.locale('ru');
dayjs.updateLocale('ru', {
    weekStart: 1,
});

export {dayjs};
export type {Dayjs} from 'dayjs';

const PARSE_FORMATS = [
    'DD.MM.YYYY HH:mm',
    'DD.MM.YYYY',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD',
];

export const isISODate = (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
};

export const formatIfDate = (value: unknown, format: string = 'DD.MM.YYYY HH:mm'): unknown => {
    if (isISODate(value)) {
        const date = dayjs(value as string);
        if (!date.isValid() || date.year() < 1900) {
            return '';
        }
        return date.format(format);
    }
    return value;
};

export const defaultMonthDateRange = () => ({
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().format('YYYY-MM-DD'),
});

export const isDefaultMonthDateRange = (dateFrom?: string, dateTo?: string) => {
    if (!dateFrom && !dateTo) {
        return false;
    }

    const defaults = defaultMonthDateRange();
    return dateFrom === defaults.date_from && dateTo === defaults.date_to;
};

/** True when the user changed the period away from the default month-to-today range. */
export const isActiveDateRangeFilter = (dateFrom?: string, dateTo?: string) => {
    if (!dateFrom && !dateTo) {
        return false;
    }

    return !isDefaultMonthDateRange(dateFrom, dateTo);
};

export const parseFlexibleDate = (dateString: string) => {
    const cleanedDate = dateString.replace(/г\./g, '').trim();
    let transactionDate = dayjs(cleanedDate, PARSE_FORMATS, true);

    if (!transactionDate.isValid()) {
        transactionDate = dayjs(cleanedDate);
    }

    return transactionDate;
};

export const eachDayInRange = (start: unknown, end: unknown): string[] => {
    const dates: string[] = [];
    let current = dayjs(start as string | number | Date).startOf('day');
    const endDate = dayjs(end as string | number | Date).startOf('day');
    while (current.isSameOrBefore(endDate, 'day')) {
        dates.push(current.format('YYYY-MM-DD'));
        current = current.add(1, 'day');
    }
    return dates;
};

export const formatTransactionDateTime = (dateString: string): { date: string; time: string } => {
    if (!dateString) return {date: '', time: ''};

    const transactionDate = parseFlexibleDate(dateString);
    if (!transactionDate.isValid()) {
        return {date: dateString, time: ''};
    }

    const today = dayjs();
    const time = transactionDate.format('HH:mm');

    if (transactionDate.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')) {
        return {date: 'Сегодня', time};
    }

    if (transactionDate.year() === today.year()) {
        const day = transactionDate.format('D');
        const month = transactionDate.format('MMMM');
        const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
        return {date: `${day} ${capitalizedMonth}`, time};
    }

    return {date: transactionDate.format('DD.MM.YYYY'), time};
};
